/**
 * useGroupFiltering Hook
 * Handles all filtering-related operations for group creation
 * Split from useManageGroupOperations.ts for better separation of concerns
 */

import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { serviceFactory } from '@/services/core/serviceFactory';
import { GroupFilteringService } from '@/services/features/GroupFilteringService';
import type { CreateGroupState, CreateGroupStateActions } from './useManageGroupState';

export interface GroupFilteringOperations {
  // Clinic and facility operations
  handleClinicSelect: (clinicId: string, clinicName: string, timezone?: string) => Promise<void>;
  loadFacilitiesAndProviders: (clinicId: number) => Promise<void>;
  loadProvidersForFacilities: (clinicId: number, facilityIds: number[], preserveSelection?: boolean) => Promise<void>;
  
  // Filter operations
  handleApplyFilters: () => Promise<void>;
  loadFilterOptions: () => Promise<void>;
  loadDependentFilters: () => Promise<void>;
  loadInsuranceDependentFilters: () => Promise<void>;
  
  // Patient operations
  handleShowPatientList: () => Promise<void>;
  handlePatientSearch: (searchTerm: string) => Promise<void>;
  handleSelectPatientFromDropdown: (searchString: string) => void;
  handleAddPatientFromSearch: () => Promise<void>;
  
  // EMR operations
  handlePasteEmrs: () => Promise<void>;
  handleShowEmrPatientList: () => Promise<void>;
  
  // Utility operations
  resetFilters: () => void;
}

export const useGroupFiltering = (
  state: CreateGroupState,
  actions: CreateGroupStateActions
): GroupFilteringOperations => {
  const { toast } = useToast();
  const { user, hasuraToken } = useAuth();
  const filteringService = serviceFactory.getService(GroupFilteringService);

  // Initialize service context
  if (user?.id && hasuraToken) {
    filteringService.updateContext({
      userId: user.id,
      hasuraToken,
      userType: user.type
    });
  }

  /**
   * Handle clinic selection
   */
  const handleClinicSelect = useCallback(async (clinicId: string, clinicName: string, timezone?: string) => {
    actions.setSelectedClinic(clinicId);
    actions.setClinicSearchTerm(clinicName);
    if (timezone) {
      actions.setTimeZone(timezone);
    }
    actions.setShowClinicDropdown(false);
    
    // Reset dependent data
    actions.setSelectedFacilities([]);
    actions.setSelectedProviders([]);
    actions.resetFilters();
    
    // Don't load facilities here - let the useEffect in ManageGroup handle it
  }, [actions]);

  /**
   * Load facilities for a clinic (only facilities, not providers)
   */
  const loadFacilitiesAndProviders = useCallback(async (clinicId: number) => {
    try {
      const facilities = await filteringService.loadFacilities(clinicId);
      actions.setFacilities(facilities as any);
      // Don't load providers here - they will be loaded when facilities are selected
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load facilities",
        variant: "destructive",
      });
    }
  }, [actions, filteringService, toast]);

  /**
   * Load providers for specific facilities
   */
  const loadProvidersForFacilities = useCallback(async (clinicId: number, facilityIds: number[], preserveSelection: boolean = false) => {
    try {
      const providers = await filteringService.loadProvidersForFacilities(clinicId, facilityIds);
      actions.setProviders(providers as any);
      if (!preserveSelection) {
        actions.setSelectedProviders([]); // Reset selected providers only if not preserving
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load providers for facilities",
        variant: "destructive",
      });
    }
  }, [actions, filteringService, toast]);

  /**
   * Apply filters to get patient count
   */
  const handleApplyFilters = useCallback(async () => {
    // Validation
    if (state.isDuplicateMode) {
      if (!state.selectedClinic) {
        toast({
          title: "Validation Error",
          description: "Please select clinic first.",
          variant: "destructive"
        });
        return;
      }
    } else {
      if (!state.selectedClinic || state.selectedFacilities.length === 0 || state.selectedProviders.length === 0) {
        toast({
          title: "Validation Error",
          description: "Please select clinic, facilities, and providers first.",
          variant: "destructive"
        });
        return;
      }
    }

    actions.setIsLoading(true);
    actions.setShowFilters(true);
    
    try {
      // First load filter options (primary dx, secondary dx, insurance options)
      const filterOptions = await filteringService.getFilterOptions(
        parseInt(state.selectedClinic),
        state.selectedFacilities.length > 0 ? state.selectedFacilities : undefined,
        state.selectedProviders.length > 0 ? state.selectedProviders : undefined
      );
      
      // Set the filter options in state
      actions.setPrimaryDxOptions(filterOptions.primaryDx);
      actions.setSecondaryDxOptions(filterOptions.secondaryDx);
      actions.setPrimaryInsuranceOptions(filterOptions.primaryInsurance);
      actions.setSecondaryInsuranceOptions(filterOptions.secondaryInsurance);
      actions.setInsuranceTypeOptions(filterOptions.insuranceType);

      // Then apply the filters to get patient count
      const filterParams = {
        clinicId: parseInt(state.selectedClinic),
        facilityIds: state.selectedFacilities.length > 0 ? state.selectedFacilities : undefined,
        providerIds: state.selectedProviders.length > 0 ? state.selectedProviders : undefined,
        primaryDx: state.selectedPrimaryDx.length > 0 ? state.selectedPrimaryDx : undefined,
        secondaryDx: state.selectedSecondaryDx.length > 0 ? state.selectedSecondaryDx : undefined,
        primaryInsurance: state.selectedPrimaryInsurance.length > 0 ? state.selectedPrimaryInsurance : undefined,
        secondaryInsurance: state.selectedSecondaryInsurance.length > 0 ? state.selectedSecondaryInsurance : undefined,
        insuranceType: state.selectedInsuranceType.length > 0 ? state.selectedInsuranceType : undefined,
        programEligibility: state.selectedProgramEligibility.length > 0 ? state.selectedProgramEligibility : undefined,
        lastVisitDate: state.lastVisitDate || undefined,
        excludeAssignedGroups: state.excludeAssignedGroups
      };

      const { patientIds, count } = await filteringService.applyFilters(filterParams);
      
      actions.setSelectedPatients(patientIds);
      actions.setFilteredPatientsCount(count);
      
      if (count === 0) {
        actions.setNumberOfPatientsToShow('');
        toast({
          title: "No Patients Found",
          description: "No patients match your filter criteria. Please adjust your filters.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Filters Applied",
          description: `Found ${count} patients matching your criteria.`,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to apply filters. Please try again.",
        variant: "destructive"
      });
    } finally {
      actions.setIsLoading(false);
    }
  }, [state, actions, filteringService, toast]);

  /**
   * Load filter options
   */
  const loadFilterOptions = useCallback(async () => {
    if (!state.selectedClinic || state.selectedFacilities.length === 0 || state.selectedProviders.length === 0) {
      return;
    }

    try {
      const filterOptions = await filteringService.getFilterOptions(
        parseInt(state.selectedClinic),
        state.selectedFacilities,
        state.selectedProviders
      );
      const primaryDxOptions = filterOptions.primaryDx;
      
      actions.setPrimaryDxOptions(primaryDxOptions);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load filter options",
        variant: "destructive",
      });
    }
  }, [state.selectedClinic, state.selectedFacilities, state.selectedProviders, actions, filteringService, toast]);

  /**
   * Load dependent filters based on selected primary/secondary diagnosis
   */
  const loadDependentFilters = useCallback(async () => {
    if (!state.selectedClinic) return;
    
    const clinicId = parseInt(state.selectedClinic);
    
    try {
      // Load filter options based on selected criteria
      if (state.selectedPrimaryDx.length > 0) {
        const filterOptions = await filteringService.getFilterOptions(clinicId);
        actions.setSecondaryDxOptions(filterOptions.secondaryDx);
        actions.setPrimaryInsuranceOptions(filterOptions.primaryInsurance);
        actions.setSecondaryInsuranceOptions(filterOptions.secondaryInsurance);
        actions.setInsuranceTypeOptions(filterOptions.insuranceType);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load dependent filter options",
        variant: "destructive",
      });
    }
  }, [state.selectedClinic, state.selectedPrimaryDx, state.selectedSecondaryDx, actions, filteringService, toast]);

  /**
   * Show patient list
   */
  const handleShowPatientList = useCallback(async () => {
    if (state.selectedPatients.length === 0) {
      toast({
        title: "No Patients",
        description: "Please apply filters first to find patients.",
        variant: "destructive"
      });
      return;
    }

    if (!state.numberOfPatientsToShow.trim() || parseInt(state.numberOfPatientsToShow) <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid number of patients to display.",
        variant: "destructive"
      });
      return;
    }

    const requestedCount = parseInt(state.numberOfPatientsToShow);
    if (requestedCount > state.filteredPatientsCount) {
      toast({
        title: "Invalid Input",
        description: `Cannot show more than ${state.filteredPatientsCount} patients. Please enter a smaller number.`,
        variant: "destructive"
      });
      return;
    }

    const limit = Math.min(requestedCount, state.selectedPatients.length);
    const patientIdsToShow = state.selectedPatients.slice(0, limit);
    const pages = Math.ceil(limit / state.patientsPerPage);
    
    actions.setTotalPages(pages);
    actions.setCurrentPage(1);
    actions.setIsLoading(true);

    try {
      const patients = await filteringService.getPatientsByIds(patientIdsToShow);
      actions.setPatients(patients as any);
      actions.setShowPatientList(true);
      
      const manuallyAddedCount = state.selectedPatients.length - (state.filteredPatientsCount - state.selectedPatients.length);
      const message = manuallyAddedCount > 0
        ? `Showing ${patients.length} patients (including ${manuallyAddedCount} manually added).`
        : `Showing ${patients.length} patients.`;
      
      toast({
        title: "Patient List Loaded",
        description: message,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load patient list. Please try again.",
        variant: "destructive"
      });
    } finally {
      actions.setIsLoading(false);
    }
  }, [state, actions, filteringService, toast]);

  /**
   * Search patients
   */
  const handlePatientSearch = useCallback(async (searchTerm: string) => {
    actions.setPatientSearchTerm(searchTerm);
    
    if (!state.selectedClinic || searchTerm.length < 2) {
      actions.setSearchResults([]);
      return;
    }

    try {
      const results = await filteringService.searchPatients(parseInt(state.selectedClinic), searchTerm);
      actions.setSearchResults(results);
    } catch (error) {
      actions.setSearchResults([]);
    }
  }, [state.selectedClinic, actions, filteringService]);

  /**
   * Select patient from dropdown
   */
  const handleSelectPatientFromDropdown = useCallback((searchString: string) => {
    actions.setSelectedPatientForAdding(searchString);
    actions.setPatientSearchTerm(searchString);
    actions.setSearchResults([]);
  }, [actions]);

  /**
   * Add patient from search
   */
  const handleAddPatientFromSearch = useCallback(async () => {
    if (!state.selectedPatientForAdding) {
      toast({
        title: "No patient selected",
        description: "Please select a patient to add",
        variant: "destructive",
      });
      return;
    }

    if (!state.selectedClinic) {
      toast({
        title: "No Clinic Selected",
        description: "Please select a clinic first",
        variant: "destructive",
      });
      return;
    }

    try {
      // Extract patient ID from search string (assuming format: "Name (EMR: 12345) (ID: 67890)")
      const searchString = state.selectedPatientForAdding;
      const idMatch = searchString.match(/\(ID:\s*(\d+)\)/);
      
      if (!idMatch) {
        toast({
          title: "Invalid Selection",
          description: "Could not extract patient ID from selection",
          variant: "destructive",
        });
        return;
      }

      const patientId = parseInt(idMatch[1]);

      // Check for duplicates
      if (state.selectedPatients.includes(patientId)) {
        toast({
          title: "Duplicate Patient",
          description: "This patient already exists in the filtered list",
          variant: "destructive",
        });
        return;
      }

      // Add patient to the selected patients array
      const updatedSelectedPatients = [...state.selectedPatients, patientId];
      actions.setSelectedPatients(updatedSelectedPatients);
      
      // Update filtered patients count
      actions.setFilteredPatientsCount(updatedSelectedPatients.length);
      
      // Update the patient table to include the newly added patient
      if (state.showPatientList) {
        try {
          const patients = await filteringService.getPatientsByIds(updatedSelectedPatients);
          actions.setPatients(patients as any);
          
          // Update selectedPatientsForGroup to include the new patient
          const currentSelectedForGroup = [...state.selectedPatientsForGroup];
          if (!currentSelectedForGroup.includes(patientId)) {
            currentSelectedForGroup.push(patientId);
            actions.setSelectedPatientsForGroup(currentSelectedForGroup);
          }
        } catch (error) {
          // Silently handle error - patient table update is not critical
        }
      }
      
      // Clear search fields
      actions.setPatientSearchTerm('');
      actions.setSearchResults([]);
      actions.setSelectedPatientForAdding('');
      
      toast({
        title: "Patient Added",
        description: "Patient has been added to the list and table updated successfully!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add patient",
        variant: "destructive",
      });
    }
  }, [state.selectedPatientForAdding, state.selectedClinic, state.selectedPatients, actions, toast]);

  /**
   * Paste EMRs from clipboard
   */
  const handlePasteEmrs = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      actions.setPatientEmrs(clipboardText);
      toast({
        title: "EMRs Pasted",
        description: "EMRs have been pasted from clipboard.",
      });
    } catch (error) {
      toast({
        title: "Paste Failed",
        description: "Could not paste from clipboard. Please paste manually.",
        variant: "destructive"
      });
    }
  }, [actions, toast]);

  /**
   * Show EMR patient list
   */
  const handleShowEmrPatientList = useCallback(async () => {
    if (!state.patientEmrs.trim()) {
      toast({
        title: "No EMRs",
        description: "Please enter patient EMRs first.",
        variant: "destructive"
      });
      return;
    }

    if (!state.selectedClinic) {
      toast({
        title: "No Clinic Selected",
        description: "Please select a clinic first.",
        variant: "destructive"
      });
      return;
    }

    // Split by both commas and newlines, then filter out empty strings
    const emrIds = state.patientEmrs
      .split(/[,\n\r]+/)
      .map(emr => emr.trim())
      .filter(emr => emr);
    
    if (emrIds.length === 0) {
      toast({
        title: "Invalid EMRs",
        description: "Please enter valid EMR IDs separated by commas or new lines.",
        variant: "destructive"
      });
      return;
    }

    actions.setIsLoading(true);
    try {
      const patients = await filteringService.getPatientsByEmrIds(emrIds, parseInt(state.selectedClinic));
      actions.setEmrPatients(patients as any);
      actions.setShowEmrPatientList(true);
      actions.setSelectedEmrPatients(patients.map((p: any) => p.patient_clinic_data_id));
      actions.setEmrSelectAll(true);
      
      toast({
        title: "Patient List Loaded",
        description: `Found ${patients.length} patients.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load patient list. Please try again.",
        variant: "destructive"
      });
    } finally {
      actions.setIsLoading(false);
    }
  }, [state.patientEmrs, state.selectedClinic, actions, filteringService, toast]);

  /**
   * Load insurance dependent filters based on selected insurance options
   */
  const loadInsuranceDependentFilters = useCallback(async () => {
    if (!state.selectedClinic) return;
    
    const clinicId = parseInt(state.selectedClinic);
    
    try {
      // Simplified insurance filtering using the clean service
      const filterOptions = await filteringService.getFilterOptions(clinicId);
      actions.setSecondaryInsuranceOptions(filterOptions.secondaryInsurance);
      actions.setInsuranceTypeOptions(filterOptions.insuranceType);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load insurance dependent filter options",
        variant: "destructive",
      });
    }
  }, [state.selectedClinic, state.selectedPrimaryDx, state.selectedSecondaryDx, state.selectedPrimaryInsurance, state.selectedSecondaryInsurance, actions, filteringService, toast]);

  /**
   * Reset filters
   */
  const resetFilters = useCallback(() => {
    actions.resetFilters();
  }, [actions]);

  return {
    // Clinic and facility operations
    handleClinicSelect,
    loadFacilitiesAndProviders,
    loadProvidersForFacilities,
    
    // Filter operations
    handleApplyFilters,
    loadFilterOptions,
    loadDependentFilters,
    loadInsuranceDependentFilters,
    
    // Patient operations
    handleShowPatientList,
    handlePatientSearch,
    handleSelectPatientFromDropdown,
    handleAddPatientFromSearch,
    
    // EMR operations
    handlePasteEmrs,
    handleShowEmrPatientList,
    
    // Utility operations
    resetFilters,
  };
};